import { FC, SyntheticEvent, useState } from "react";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import { useTranslation } from "react-i18next";
import FullScreenDialog from "../../../../../components/common/FullScreenDialog";
import { ApiRoutes } from "../../../../../datasources/networking/apiRoutes";
import { UpsertUserPayload } from "../../../../../datasources/requests/adminUsers";
import { emptyUserRegistry, UserRegistry } from "../../../../../datasources/responses/adminUsers";
import { useResource } from "../../../../../hooks/useResource";
import FiltersComponent from "../filter/FiltersComponent";
import UserDetails from "./UserDetails";

interface Props {
	id?: string;
	isOpen: boolean;
	onCancel: () => void;
	onSuccess: () => void;
}

// Use strings for tab values
enum tabIndex {
	Details,
	Filters,
}

const UserDialog: FC<Props> = ({ id, isOpen, onCancel, onSuccess }) => {
	const { t } = useTranslation("feature.users");
	const [tabValue, setTabValue] = useState<tabIndex>(tabIndex.Details);

	const {
		data: user,
		isLoading: userIsLoading,
		post,
		put,
		del,
		reload,
	} = useResource<UpsertUserPayload, UserRegistry>(
		{ route: ApiRoutes.AdminUsers, resourceId: id },
		{ emptyFallback: emptyUserRegistry, requireResourceId: true },
		{ subjectKey: "user" }
	);

	const firstName = user?.firstName ?? "";
	const lastName = user?.lastName ?? "";
	const title = id ? t(user?.isProtected ? "view user" : "edit user", { firstName, lastName }) : t("new user");

	const handleClickSend = (payload: UpsertUserPayload) => {
		if (id) {
			put(payload, { onSuccess });
		} else {
			post(payload, { onSuccess });
		}
	};

	const [askConfirmDelete, setAskConfirmDelete] = useState(false);
	const closeConfirmDelete = () => setAskConfirmDelete(false);
	const confirmDelete = () => {
		closeConfirmDelete();
		del({ onSuccess });
	};
	const handleTabChange = (event: SyntheticEvent, newValue: tabIndex) => {
		setTabValue(newValue);
	};

	return (
		<FullScreenDialog
			title={title}
			isOpen={isOpen}
			isLoading={userIsLoading}
			onClose={onCancel}
			onReload={id ? reload : undefined}
		>
			<TabContext value={tabValue}>
				<Box sx={{ borderBottom: 1, borderColor: "divider" }}>
					<TabList onChange={handleTabChange} aria-label="user dialog tabs">
						<Tab label={t("registry")} value={tabIndex.Details} />
						{id && <Tab label={t("filters")} value={tabIndex.Filters} />}
					</TabList>
				</Box>
				{id && (
					<TabPanel value={tabIndex.Filters}>
						<FiltersComponent id={id ?? ""} onSuccess={onSuccess} />
					</TabPanel>
				)}
				<TabPanel value={tabIndex.Details}>
					<UserDetails
						id={id ?? ""}
						isOpen={isOpen}
						onCancel={onCancel}
						onSuccess={onSuccess}
						user={user ?? emptyUserRegistry}
						userIsLoading={userIsLoading}
						handleClickSend={handleClickSend}
						confirmDelete={confirmDelete}
						setAskConfirmDelete={setAskConfirmDelete}
						askConfirmDelete={askConfirmDelete}
						closeConfirmDelete={closeConfirmDelete}
					/>
				</TabPanel>
			</TabContext>
		</FullScreenDialog>
	);
};

export default UserDialog;
